// Casino Dashboard › Transactions
//
// Ported from casino_dashboard/src/layouts/transactions — per-player coin
// movement for a chosen period, from GET /dashboard/transaction/{days}.
import React from "react";
import { Box } from "@mui/material";
import PageHeader from "../../components/PageHeader";
import SectionCard from "../../components/SectionCard";
import DataTable from "../../components/DataTable";
import DashboardService from "../../Utility/dashboardService";
import { RangeButtons, StateLine, errText, fixed } from "./shared";

const BUTTONS = ["Today", "Yesterday", "Last Week", "Last Month"];
const DAYS_FOR = { 0: 0, 1: 1, 2: 7, 3: 30 };

const columns = [
  { header: "Name", accessor: "name", align: "left" },
  { header: "Email", accessor: "email", hidden: true },
  { header: "State", accessor: "stateName", align: "left" },
  { header: "Level", accessor: "vipLavel", align: "left" },
  { header: "SC (Redeemable)", accessor: "sc_main", align: "center" },
  { header: "SC (Playable)", accessor: "sc_credit", align: "center" },
  { header: "Deposit", accessor: "totalDeposit", align: "center" },
  { header: "SC Buy", accessor: "totalScCoins", align: "center" },
  { header: "Withdrawal", accessor: "totalWithdrawal", align: "center" },
  { header: "Login SC", accessor: "loginSC", align: "center" },
  { header: "Signup SC", accessor: "signupSC", align: "center" },
  { header: "Refer SC", accessor: "referSC", align: "center" },
  { header: "Transfer SC", accessor: "transferSC", align: "center" },
  { header: "GC to SC", accessor: "gctoSC", align: "center" },
  { header: "Post SC", accessor: "postSC", align: "center" },
  { header: "Mail SC", accessor: "mailSC", align: "center" },
];

export default function CasinoTransactions() {
  const [rows, setRows] = React.useState([]);
  const [activeIndex, setActiveIndex] = React.useState(0);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  const fetchData = React.useCallback(async (index) => {
    setLoading(true);
    setError(null);
    setActiveIndex(index);
    setRows([]);
    try {
      const result = await DashboardService.getData(`/dashboard/transaction/${DAYS_FOR[index]}`);
      setRows(Array.isArray(result?.data) ? result.data : []);
    } catch (e) {
      setError(errText(e));
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    fetchData(0);
  }, [fetchData]);

  const tableRows = React.useMemo(
    () =>
      rows.map((t) => ({
        name: `${t.name} (${t.id_user})`,
        email: t.email,
        stateName: `${t.stateName} (${t.stateCode})`,
        vipLavel: t.vipLavel,
        sc_main: fixed(t.sc_main),
        sc_credit: fixed(t.sc_credit),
        totalDeposit: `$${fixed(t.totalDeposit)}`,
        totalScCoins: fixed(t.totalScCoins),
        totalWithdrawal: `$${fixed(t.totalWithdrawal)}`,
        loginSC: fixed(t.loginSC),
        signupSC: fixed(t.signupSC),
        referSC: fixed(t.referSC),
        transferSC: fixed(t.transferSC),
        gctoSC: fixed(t.gctoSC),
        postSC: fixed(t.postSC),
        mailSC: fixed(t.mailSC),
      })),
    [rows]
  );

  return (
    <Box>
      <PageHeader
        title="Transactions"
        crumb="Casino Dashboard / Transactions"
        subtitle="Per-player coin movement for the selected period"
      />

      <Box sx={{ mb: 1.75 }}>
        <RangeButtons
          labels={BUTTONS}
          active={activeIndex}
          onChange={fetchData}
          disabled={loading}
        />
      </Box>

      <SectionCard title="Transactions" sx={{ p: 2, height: "auto" }}>
        <StateLine error={error} />
        {!error && (
          <DataTable
            columns={columns}
            rows={tableRows}
            loading={loading}
            searchable
            sortable
            paginated
            entriesPerPage
            showTotalEntries
            searchPlaceholder="Search name, email, state…"
            emptyText="No transactions for this period."
          />
        )}
      </SectionCard>
    </Box>
  );
}
